import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import { isNumber, Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { ITourContent } from 'app/shared/model/tour-content.model';
import { getEntities as getTourContents } from 'app/entities/tour-content/tour-content.reducer';
import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { IPlace } from 'app/shared/model/place.model';
import { getEntities as getPlaces } from 'app/entities/place/place.reducer';
import { ITourExtra } from 'app/shared/model/tour-extra.model';
import { getEntities as getTourExtras } from 'app/entities/tour-extra/tour-extra.reducer';
import { ITag } from 'app/shared/model/tag.model';
import { getEntities as getTags } from 'app/entities/tag/tag.reducer';
import { IPromotion } from 'app/shared/model/promotion.model';
import { getEntities as getPromotions } from 'app/entities/promotion/promotion.reducer';
import { ITourCategory } from 'app/shared/model/tour-category.model';
import { getEntities as getTourCategories } from 'app/entities/tour-category/tour-category.reducer';
import { IDestination } from 'app/shared/model/destination.model';
import { getEntities as getDestinations } from 'app/entities/destination/destination.reducer';
import { TourKind } from 'app/shared/model/enumerations/tour-kind.model';
import { TourMode } from 'app/shared/model/enumerations/tour-mode.model';
import { DurationMeasure } from 'app/shared/model/enumerations/duration-measure.model';
import { getEntity, updateEntity, createEntity, reset } from './tour.reducer';

// eslint-disable-next-line complexity
const TourUpdate = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const tourContents = useAppSelector(state => state.tourContent.entities);
  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const places = useAppSelector(state => state.place.entities);
  const tourExtras = useAppSelector(state => state.tourExtra.entities);
  const tags = useAppSelector(state => state.tag.entities);
  const promotions = useAppSelector(state => state.promotion.entities);
  const tourCategories = useAppSelector(state => state.tourCategory.entities);
  const destinations = useAppSelector(state => state.destination.entities);
  const tourEntity = useAppSelector(state => state.tour.entity);
  const loading = useAppSelector(state => state.tour.loading);
  const updating = useAppSelector(state => state.tour.updating);
  const updateSuccess = useAppSelector(state => state.tour.updateSuccess);
  const tourKindValues = Object.keys(TourKind);
  const tourModeValues = Object.keys(TourMode);
  const durationMeasureValues = Object.keys(DurationMeasure);

  const [formData, setFormData] = useState<any>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(',')[1];
        setFormData({
          ...formData,
          defaultImageData: base64Data,
          defaultImageDataContentType: file.type,
          defaultImage: file.name,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    setFormData({
      ...formData,
      defaultImageData: null,
      defaultImageDataContentType: null,
      defaultImage: '',
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    navigate('/tour' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }
    dispatch(getTourContents({}));
    dispatch(getUsers({}));
    dispatch(getPlaces({}));
    dispatch(getTourExtras({}));
    dispatch(getTags({}));
    dispatch(getPromotions({}));
    dispatch(getTourCategories({}));
    dispatch(getDestinations({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const getInitialFormData = () => {
    if (!isNew && tourEntity) {
      return {
        ...tourEntity,
        content: tourEntity?.content?.id,
        createdBy: tourEntity?.createdBy?.id,
        meetingPoint: tourEntity?.meetingPoint?.id,
        finishPoint: tourEntity?.finishPoint?.id,
        tourExtras: tourEntity?.tourExtras?.map((e: any) => e.id.toString()),
        tags: tourEntity?.tags?.map((e: any) => e.id.toString()),
        promotions: tourEntity?.promotions?.map((e: any) => e.id.toString()),
        categories: tourEntity?.categories?.map((e: any) => e.id.toString()),
        destination: tourEntity?.destination?.id,
        defaultCategory: tourEntity?.defaultCategory?.id,
      };
    }
    return {
      createdBy: account?.id,
      createdDate: new Date().toISOString().split('T')[0],
    };
  };

  useEffect(() => {
    setFormData(getInitialFormData());
  }, [tourEntity, isNew]);

  const handleChange = (field: string) => (event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleMultiSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const convertToNumber = (value: any, field: string): any => {
    const numberFields = ['id', 'duration', 'initialPrice', 'price', 'rating'];
    if (numberFields.includes(field) && value !== undefined && typeof value !== 'number') {
      return Number(value);
    }
    return value;
  };

  const findEntityById = (entities: any[], value: any) => {
    return entities.find(it => it.id.toString() === value?.toString());
  };

  const saveEntity = () => {
    const values = { ...formData };
    ['id', 'duration', 'initialPrice', 'price', 'rating', 'priority'].forEach(field => {
      if (values[field] !== undefined) {
        values[field] = convertToNumber(values[field], field);
      }
    });

    const booleanFields = [
      'enabled',
      'petFriendly',
      'kidsAllowed',
      'smoking',
      'app',
      'accessibility',
      'audioGuide',
      'tourGuide',
      'displayOnHomepage',
    ];
    booleanFields.forEach(field => {
      values[field] = values[field] === true;
    });

    const entity = {
      ...tourEntity,
      ...values,
      content: findEntityById(tourContents, values.content),
      createdBy: findEntityById(users, values.createdBy),
      meetingPoint: findEntityById(places, values.meetingPoint),
      finishPoint: findEntityById(places, values.finishPoint),
      tourExtras: values.tourExtras ? mapIdList(values.tourExtras) : [],
      tags: values.tags ? mapIdList(values.tags) : [],
      promotions: values.promotions ? mapIdList(values.promotions) : [],
      categories: values.categories ? mapIdList(values.categories) : [],
      destination: findEntityById(destinations, values.destination),
      defaultCategory: findEntityById(tourCategories, values.defaultCategory),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <h2 id="jhipsterSampleApplicationApp.tour.home.createOrEditLabel" data-cy="TourCreateUpdateHeading">
          <Translate
            contentKey={isNew ? 'jhipsterSampleApplicationApp.tour.home.createLabel' : 'jhipsterSampleApplicationApp.tour.home.editLabel'}
          >
            {isNew ? 'Create Tour' : 'Edit Tour'}
          </Translate>
        </h2>
        {!isNew && formData.id && (
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 2, py: 0.5, borderRadius: 1, fontWeight: 'bold' }}>
            ID: {formData.id}
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>Loading...</Box>
      ) : (
        <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.code')}
                    id="tour-code"
                    name="code"
                    data-cy="code"
                    value={formData.code || ''}
                    onChange={handleChange('code')}
                    fullWidth
                    required
                    error={!formData.code}
                    helperText={!formData.code ? translate('entity.validation.required') : ''}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.priority')}
                    id="tour-priority"
                    name="priority"
                    data-cy="priority"
                    value={formData.priority || ''}
                    onChange={handleChange('priority')}
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enabled || false}
                        onChange={handleChange('enabled')}
                        id="tour-enabled"
                        name="enabled"
                        data-cy="enabled"
                      />
                    }
                    label={translate('jhipsterSampleApplicationApp.tour.enabled')}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="tour-kind-label">{translate('jhipsterSampleApplicationApp.tour.kind')}</InputLabel>
                    <Select
                      labelId="tour-kind-label"
                      id="tour-kind"
                      name="kind"
                      data-cy="kind"
                      value={formData.kind || 'TOUR'}
                      label={translate('jhipsterSampleApplicationApp.tour.kind')}
                      onChange={handleSelectChange('kind')}
                    >
                      {tourKindValues.map(tourKind => (
                        <MenuItem value={tourKind} key={tourKind}>
                          {translate('jhipsterSampleApplicationApp.TourKind.' + tourKind)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="tour-mode-label">{translate('jhipsterSampleApplicationApp.tour.mode')}</InputLabel>
                    <Select
                      labelId="tour-mode-label"
                      id="tour-mode"
                      name="mode"
                      data-cy="mode"
                      value={formData.mode || 'BUS'}
                      label={translate('jhipsterSampleApplicationApp.tour.mode')}
                      onChange={handleSelectChange('mode')}
                    >
                      {tourModeValues.map(tourMode => (
                        <MenuItem value={tourMode} key={tourMode}>
                          {translate('jhipsterSampleApplicationApp.TourMode.' + tourMode)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextField
                label={translate('jhipsterSampleApplicationApp.tour.icon')}
                id="tour-icon"
                name="icon"
                data-cy="icon"
                value={formData.icon || ''}
                onChange={handleChange('icon')}
                fullWidth
              />

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.duration')}
                    id="tour-duration"
                    name="duration"
                    data-cy="duration"
                    value={formData.duration || ''}
                    onChange={handleChange('duration')}
                    fullWidth
                    required
                    type="number"
                    error={!formData.duration || !isNumber(formData.duration)}
                    helperText={
                      !formData.duration
                        ? translate('entity.validation.required')
                        : !isNumber(formData.duration)
                          ? translate('entity.validation.number')
                          : ''
                    }
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="tour-durationMeasure-label">
                      {translate('jhipsterSampleApplicationApp.tour.durationMeasure')}
                    </InputLabel>
                    <Select
                      labelId="tour-durationMeasure-label"
                      id="tour-durationMeasure"
                      name="durationMeasure"
                      data-cy="durationMeasure"
                      value={formData.durationMeasure || 'MINUTES'}
                      label={translate('jhipsterSampleApplicationApp.tour.durationMeasure')}
                      onChange={handleSelectChange('durationMeasure')}
                    >
                      {durationMeasureValues.map(durationMeasure => (
                        <MenuItem value={durationMeasure} key={durationMeasure}>
                          {translate('jhipsterSampleApplicationApp.DurationMeasure.' + durationMeasure)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.badge')}
                    id="tour-badge"
                    name="badge"
                    data-cy="badge"
                    value={formData.badge || ''}
                    onChange={handleChange('badge')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.rating')}
                    id="tour-rating"
                    name="rating"
                    data-cy="rating"
                    value={formData.rating || ''}
                    onChange={handleChange('rating')}
                    fullWidth
                    type="number"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.initialPrice')}
                    id="tour-initialPrice"
                    name="initialPrice"
                    data-cy="initialPrice"
                    value={formData.initialPrice || ''}
                    onChange={handleChange('initialPrice')}
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.price')}
                    id="tour-price"
                    name="price"
                    data-cy="price"
                    value={formData.price || ''}
                    onChange={handleChange('price')}
                    fullWidth
                    type="number"
                  />
                </Grid>
              </Grid>

              <TextField
                label={translate('jhipsterSampleApplicationApp.tour.widgetId')}
                id="tour-widgetId"
                name="widgetId"
                data-cy="widgetId"
                value={formData.widgetId || ''}
                onChange={handleChange('widgetId')}
                fullWidth
              />

              <TextField
                label={translate('jhipsterSampleApplicationApp.tour.externalId')}
                id="tour-externalId"
                name="externalId"
                data-cy="externalId"
                value={formData.externalId || ''}
                onChange={handleChange('externalId')}
                fullWidth
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {translate('jhipsterSampleApplicationApp.tour.defaultImageData')}
                </Typography>
                {formData.defaultImageData ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <img
                      src={`data:${formData.defaultImageDataContentType};base64,${formData.defaultImageData}`}
                      alt="Default"
                      style={{ maxHeight: '100px', borderRadius: '4px' }}
                    />
                    <IconButton onClick={handleClearImage} color="error" size="small">
                      <FontAwesomeIcon icon="trash" />
                    </IconButton>
                  </Box>
                ) : null}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="tour-defaultImageData-input"
                />
                <label htmlFor="tour-defaultImageData-input">
                  <Button variant="outlined" component="span" startIcon={<FontAwesomeIcon icon="upload" />}>
                    Upload Image
                  </Button>
                </label>
              </Box>

              <TextField
                label={translate('jhipsterSampleApplicationApp.tour.cssStyle')}
                id="tour-cssStyle"
                name="cssStyle"
                data-cy="cssStyle"
                value={formData.cssStyle || ''}
                onChange={handleChange('cssStyle')}
                fullWidth
                multiline
                rows={4}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-destination-label">{translate('jhipsterSampleApplicationApp.tour.destination')}</InputLabel>
                <Select
                  labelId="tour-destination-label"
                  id="tour-destination"
                  name="destination"
                  data-cy="destination"
                  value={formData.destination || ''}
                  label={translate('jhipsterSampleApplicationApp.tour.destination')}
                  onChange={handleSelectChange('destination')}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {destinations?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-defaultCategory-label">{translate('jhipsterSampleApplicationApp.tour.defaultCategory')}</InputLabel>
                <Select
                  labelId="tour-defaultCategory-label"
                  id="tour-defaultCategory"
                  name="defaultCategory"
                  data-cy="defaultCategory"
                  value={formData.defaultCategory || ''}
                  label={translate('jhipsterSampleApplicationApp.tour.defaultCategory')}
                  onChange={handleSelectChange('defaultCategory')}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {tourCategories?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.petFriendly || false}
                          onChange={handleChange('petFriendly')}
                          id="tour-petFriendly"
                          name="petFriendly"
                          data-cy="petFriendly"
                        />
                      }
                      label={translate('jhipsterSampleApplicationApp.tour.petFriendly')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.kidsAllowed || false}
                          onChange={handleChange('kidsAllowed')}
                          id="tour-kidsAllowed"
                          name="kidsAllowed"
                          data-cy="kidsAllowed"
                        />
                      }
                      label={translate('jhipsterSampleApplicationApp.tour.kidsAllowed')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.smoking || false}
                          onChange={handleChange('smoking')}
                          id="tour-smoking"
                          name="smoking"
                          data-cy="smoking"
                        />
                      }
                      label={translate('jhipsterSampleApplicationApp.tour.smoking')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch checked={formData.app || false} onChange={handleChange('app')} id="tour-app" name="app" data-cy="app" />
                      }
                      label={translate('jhipsterSampleApplicationApp.tour.app')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.accessibility || false}
                          onChange={handleChange('accessibility')}
                          id="tour-accessibility"
                          name="accessibility"
                          data-cy="accessibility"
                        />
                      }
                      label={translate('jhipsterSampleApplicationApp.tour.accessibility')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.audioGuide || false}
                          onChange={handleChange('audioGuide')}
                          id="tour-audioGuide"
                          name="audioGuide"
                          data-cy="audioGuide"
                        />
                      }
                      label={translate('jhipsterSampleApplicationApp.tour.audioGuide')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.tourGuide || false}
                          onChange={handleChange('tourGuide')}
                          id="tour-tourGuide"
                          name="tourGuide"
                          data-cy="tourGuide"
                        />
                      }
                      label={translate('jhipsterSampleApplicationApp.tour.tourGuide')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.displayOnHomepage || false}
                          onChange={handleChange('displayOnHomepage')}
                          id="tour-displayOnHomepage"
                          name="displayOnHomepage"
                          data-cy="displayOnHomepage"
                        />
                      }
                      label={translate('jhipsterSampleApplicationApp.tour.displayOnHomepage')}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.availableFromDate')}
                    id="tour-availableFromDate"
                    name="availableFromDate"
                    data-cy="availableFromDate"
                    value={formData.availableFromDate || ''}
                    onChange={handleChange('availableFromDate')}
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.availableToDate')}
                    id="tour-availableToDate"
                    name="availableToDate"
                    data-cy="availableToDate"
                    value={formData.availableToDate || ''}
                    onChange={handleChange('availableToDate')}
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.departure')}
                    id="tour-departure"
                    name="departure"
                    data-cy="departure"
                    value={formData.departure || ''}
                    onChange={handleChange('departure')}
                    fullWidth
                    type="time"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tour.returnTime')}
                    id="tour-returnTime"
                    name="returnTime"
                    data-cy="returnTime"
                    value={formData.returnTime || ''}
                    onChange={handleChange('returnTime')}
                    fullWidth
                    type="time"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-content-label">{translate('jhipsterSampleApplicationApp.tour.content')}</InputLabel>
                <Select
                  labelId="tour-content-label"
                  id="tour-content"
                  name="content"
                  data-cy="content"
                  value={formData.content || ''}
                  label={translate('jhipsterSampleApplicationApp.tour.content')}
                  onChange={handleSelectChange('content')}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {tourContents?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-meetingPoint-label">{translate('jhipsterSampleApplicationApp.tour.meetingPoint')}</InputLabel>
                <Select
                  labelId="tour-meetingPoint-label"
                  id="tour-meetingPoint"
                  name="meetingPoint"
                  data-cy="meetingPoint"
                  value={formData.meetingPoint || ''}
                  label={translate('jhipsterSampleApplicationApp.tour.meetingPoint')}
                  onChange={handleSelectChange('meetingPoint')}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {places?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-finishPoint-label">{translate('jhipsterSampleApplicationApp.tour.finishPoint')}</InputLabel>
                <Select
                  labelId="tour-finishPoint-label"
                  id="tour-finishPoint"
                  name="finishPoint"
                  data-cy="finishPoint"
                  value={formData.finishPoint || ''}
                  label={translate('jhipsterSampleApplicationApp.tour.finishPoint')}
                  onChange={handleSelectChange('finishPoint')}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {places?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-tourExtra-label">{translate('jhipsterSampleApplicationApp.tour.tourExtra')}</InputLabel>
                <Select
                  labelId="tour-tourExtra-label"
                  id="tour-tourExtra"
                  data-cy="tourExtra"
                  multiple
                  value={formData.tourExtras || []}
                  onChange={handleMultiSelectChange('tourExtras')}
                  label={translate('jhipsterSampleApplicationApp.tour.tourExtra')}
                  renderValue={selected => {
                    const selectedExtras = tourExtras?.filter((t: any) => selected.includes(t.id.toString())) || [];
                    return selectedExtras.map((t: any) => t.code).join(', ');
                  }}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {tourExtras?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-tags-label">{translate('jhipsterSampleApplicationApp.tour.tags')}</InputLabel>
                <Select
                  labelId="tour-tags-label"
                  id="tour-tags"
                  data-cy="tags"
                  multiple
                  value={formData.tags || []}
                  onChange={handleMultiSelectChange('tags')}
                  label={translate('jhipsterSampleApplicationApp.tour.tags')}
                  renderValue={selected => {
                    const selectedTags = tags?.filter((t: any) => selected.includes(t.id.toString())) || [];
                    return selectedTags.map((t: any) => t.code).join(', ');
                  }}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {tags?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-promotions-label">{translate('jhipsterSampleApplicationApp.tour.promotions')}</InputLabel>
                <Select
                  labelId="tour-promotions-label"
                  id="tour-promotions"
                  data-cy="promotions"
                  multiple
                  value={formData.promotions || []}
                  onChange={handleMultiSelectChange('promotions')}
                  label={translate('jhipsterSampleApplicationApp.tour.promotions')}
                  renderValue={selected => {
                    const selectedPromos = promotions?.filter((p: any) => selected.includes(p.id.toString())) || [];
                    return selectedPromos.map((p: any) => p.code).join(', ');
                  }}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {promotions?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tour-category-label">{translate('jhipsterSampleApplicationApp.tour.category')}</InputLabel>
                <Select
                  labelId="tour-category-label"
                  id="tour-category"
                  data-cy="category"
                  multiple
                  value={formData.categories || []}
                  onChange={handleMultiSelectChange('categories')}
                  label={translate('jhipsterSampleApplicationApp.tour.category')}
                  renderValue={selected => {
                    const selectedCats = tourCategories?.filter((c: any) => selected.includes(c.id.toString())) || [];
                    return selectedCats.map((c: any) => c.code).join(', ');
                  }}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {tourCategories?.map(otherEntity => (
                    <MenuItem value={otherEntity.id} key={otherEntity.id}>
                      {otherEntity.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  component={Link}
                  id="cancel-save"
                  data-cy="entityCreateCancelButton"
                  to="/tour"
                  replace
                  color="info"
                  variant="outlined"
                  startIcon={<FontAwesomeIcon icon="arrow-left" />}
                >
                  <Translate contentKey="entity.action.back">Back</Translate>
                </Button>
                <Button
                  color="primary"
                  id="save-entity"
                  data-cy="entityCreateSaveButton"
                  onClick={saveEntity}
                  disabled={updating}
                  variant="contained"
                  startIcon={<FontAwesomeIcon icon="save" />}
                >
                  <Translate contentKey="entity.action.save">Save</Translate>
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default TourUpdate;
